import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router'
import { useMutation } from '@tanstack/react-query'

import useQuery from '@shared/hooks/src/useQuery'
import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { useMe } from '@shared/providers/src/MeProvider'
import API from '@shared/services/src/API'
import { EncounterTypeToTreatmentType, getKeyByValue, handleError, includesOneOfErrorMessages, QK } from '@shared/utils'

import useNewGTMEvent from '@hooks/useNewGTMEvent'
import useTreatments from '@hooks/useTreatments'

import { invalidateCache, useUpdateAppointment } from '../Scheduled/ConfirmSchedule.hooks'

export function useOnDemandAppointmentCreation(options = {}) {
  const send = useNewGTMEvent()
  const navigate = useNavigate()

  const create = useMutation({
    mutationFn: (data) => API.appointments.onDemand.create(data),
    ...options,
  })

  return (props) =>
    create
      .mutateAsync(props)
      .then((onDemandLog) => {
        send('on-demand-created', { on_demand_log_id: onDemandLog.id })
        return navigate(`/app/on-demand/${onDemandLog.id}`)
      })
      .catch(catchError)
}

export function useSwapToOnDemand(appointmentId) {
  const me = useMe()
  const send = useNewGTMEvent()
  const navigate = useNavigate()

  const update = useUpdateAppointment(appointmentId)

  return (props) =>
    update
      .mutateAsync({ on_demand_swap: true, ...props })
      .then((onDemandLog) => {
        send('on-demand-swapped', { on_demand_log_id: onDemandLog.id, appointment_id: appointmentId })
        navigate(`/app/on-demand/${onDemandLog.id}`)
        invalidateCache(me.id)
      })
      .catch(catchError)
}

export function useIsOnDemandAvailable(encounterTypeId) {
  const me = useMe()
  const encounterTypes = useLookup(Lookup.EncounterTypes)
  const treatments = useTreatments()

  const currentTreatment = useMemo(() => {
    if (encounterTypeId) {
      const encounterTypeName = getKeyByValue(encounterTypes, encounterTypeId)
      return EncounterTypeToTreatmentType[encounterTypeName]
    }

    return treatments?.find((t) => t.active)?.name
  }, [encounterTypeId, encounterTypes, treatments])

  return useQuery({
    queryKey: QK.patients.id(me.id).availableServiceLines.list(),
    queryFn: () => API.patients.id(me.id).availableServices(),
    select: (data) => data.find((s) => s.name === currentTreatment)?.onDemand || false,
  })
}

const catchError = (e) => {
  if (includesOneOfErrorMessages(e, ["The patient's CBO cannot schedule on demand appointments at the moment"])) {
    toast.error('We apologize, but there are no available providers right now')
  } else {
    handleError(e)
  }
}
